import { OnInit, OnDestroy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { ISalonDetailsApi } from '@app/salon-details/models/salon-details-api.model';
import { ITreatmentCategory } from '@app/salon-details/models/treatment-category.model';
import { IDepartment, ISalonPage, ISalonReviewsSummary, ISalonReviews, ITreatmentApiModel } from '@app/shared/models';
import { DialogsService } from '@app/shared/services/dialogs/dialogs.service';
import { OkDialogResult } from '@app/shared/services/dialogs/ok-dialog/ok-dialog-result.enum';
import { YesNoDialogResult } from '@app/shared/services/dialogs/yes-no-dialog/yes-no-dialog-result.enum';
import { ISeoTagType } from '@app/shared/services/seo/seo-tag-type.enum';
import { SEOService } from '@app/shared/services/seo/seo.service';
import { Observable, of, Subscription, map, switchMap, tap } from 'rxjs';
import { IOkDialogConfig } from '@app/shared/services/dialogs/ok-dialog/ok-dialog-config';
import { getGoogleMapsUrlFromSalonAdress } from '@app/shared/utils/salon';
import { SalonService } from '@app/shared/services/salon/salon.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerStopIntentService } from '../services/customer-stop-dialog-intent.service';
import { CustomerStopDialogContainerComponent } from './customer-stop-dialog-container/customer-stop-dialog-container.component';
import { IBeginBookingFlowInfo } from '@app/shared/services/booking/begin-booking-flow-info.model';
@Component({
  selector: 'app-salon-details-base',
  template: '',
  standalone: true
})
export class SalonDetailsBaseComponent implements OnInit, OnDestroy {

  public reviewsSummary: ISalonReviewsSummary;
  public reviews: ISalonReviews;
  public salonPage: ISalonPage;
  public salonDetails: ISalonDetailsApi;
  public treatmentCategories: ITreatmentCategory[];
  public imageUrl = '';
  public logoUrl = '';
  public formattedSalonAddress = '';
  public formattedSalonDepartments = '';
  public formattedSalonDepartmentsForAboutSalon = '';
  public googleMapsUrl = '';
  public selectedTreatments: ITreatmentApiModel[] = [];

  private masterSubscription = new Subscription();
  private customerStopIntentService = inject(CustomerStopIntentService);
  private ngbModal = inject(NgbModal);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private seoService: SEOService,
    private dialogsService: DialogsService,
    private salonService: SalonService
  ) { }

  ngOnInit(): void {
    this.setSEO();
    this.salonPage = this.route.snapshot.data.salonPage;
    this.salonDetails = this.salonPage.salonDetails;
    this.treatmentCategories = this.salonPage.treatmentCategories;
    this.logoUrl = this.salonDetails.logoUrl;
    this.imageUrl = this.salonDetails.photoUrl;
    this.googleMapsUrl = this.getGoogleMapsUrl();
    this.formattedSalonAddress = this.getFormattedSalonAddress();
    this.formattedSalonDepartments = this.getFormattedSalonDepartments();
    this.formattedSalonDepartmentsForAboutSalon = this.formattedSalonDepartments.replace('Kapsalon', 'Kapper');
    this.reviews = this.salonPage.salonReviews;
    this.handleSalonPageMessageDisplay();
    this.createEventSubToCustomerStopDialog();
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  public goBack(): void {
    let salonName = sessionStorage.getItem('recentSalonName');
    const departmentName = sessionStorage.getItem('recentDepartmentName');
    salonName = salonName ? salonName : this.salonDetails.city;

    if (departmentName) {
      this.router.navigate(['/salons', salonName, departmentName]);
      return;
    }
    this.router.navigate(['/salons', salonName]);
  }

  public bookTreatment(treatments: ITreatmentApiModel[]): void {
    this.askUserIfBeenToSalonBefore$()
      .pipe(
        tap((salonWasVisited: boolean | null) => {
          if (salonWasVisited === null) { return }
          else if (this.salonDetails.newCustomersAllowed || salonWasVisited) {
            this.beginBookingFlow(treatments, !salonWasVisited);
          } else {
            this.customerStopIntentService.emitOpenCustomerStopDialongIntentEvent$(treatments);
          }
        }))
      .subscribe();
  }

  public onTreatmentShowDescriptionClick(treatment: ITreatmentApiModel): void {
    const okDialogConfig: IOkDialogConfig = {
      okButtonTextTranslationKey: 'SALON-DETAILS.TREATMENT-DESCRIPTION-MODAL.BUTTON-CLOSE',
      acceptHTML: true,
      modalBodyClass: 'notranslate'
    };
    this.dialogsService.showOkDialog(
      'SALON-DETAILS.TREATMENT-DESCRIPTION-MODAL.TEXT',
      okDialogConfig, {
      treatmentName: treatment.name,
      treatmentDescription: treatment.description
    })
      .subscribe();
  }

  public addToMultipleTreatmentsArray({ event, selectedTreatment }): void {
    if (event.target.checked) {
      this.selectedTreatments.push(selectedTreatment);
      this.selectedTreatments = [...this.selectedTreatments];
    } else if (!event.target.checked) {
      const index = this.selectedTreatments.map((treatment) => treatment.id).indexOf(selectedTreatment.id);
      if (index !== -1) {
        this.selectedTreatments.splice(index, 1);
      }
      this.selectedTreatments = [...this.selectedTreatments];
    }
  }

  private askUserIfBeenToSalonBefore$(): Observable<boolean | null> {
    return this.salonService.getIfSalonWasVisited$(this.salonDetails.id)
      .pipe(
        switchMap((salonWasVisited: boolean) => salonWasVisited
          ? of(true)
          : this.dialogsService.showYesNoDialog('SALON-DETAILS.NEW-CUSTOMER-SCENARIO.QUESTION')
            .pipe(
              map((result: YesNoDialogResult) => result === YesNoDialogResult.Close ? null : Boolean(result)
              ))
        ));
  }

  private beginBookingFlow(selectedTreatments: ITreatmentApiModel[], isNewCustomer: boolean): void {
    const dataForStartingBookingFlow: IBeginBookingFlowInfo = {
      selectedTreatments,
      isNewCustomer,
      salonDetails: this.salonDetails
    };
    this.router.navigate(['booking'], { state: { dataForStartingBookingFlow }, relativeTo: this.route, replaceUrl: true });
  }


  private handleSalonPageMessageDisplay(): void {
    if (this.salonDetails.salonPageMessage.isActive &&
      window.sessionStorage.getItem(this.salonPageMessageIsReadFlagName) !== 'true') {
      this.displaySalonPageMessage(this.salonDetails.salonPageMessage.messageContent.trim());
    }
  }

  private displaySalonPageMessage(messageContent: string): void {
    this.dialogsService.showOkDialog(messageContent, { dissmissable: false, modalBodyClass: 'notranslate' })
      .subscribe((result: OkDialogResult) => {
        switch (result) {
          case OkDialogResult.Close:
            break;
          default:
            window.sessionStorage.setItem(this.salonPageMessageIsReadFlagName, 'true');
            break;
        }
      });
  }

  private get salonPageMessageIsReadFlagName() {
    return 'salonPageMessageIsRead' + this.salonDetails.id;
  }

  private getGoogleMapsUrl(): string {
    return getGoogleMapsUrlFromSalonAdress({
      salonCity: this.salonDetails.city,
      salonStreet: this.salonDetails.street,
      salonHouseNumber: this.salonDetails.houseNumber.toString(),
      salonHouseSubNumber: this.salonDetails.houseNumberSub
    });
  }

  private getFormattedSalonAddress(): string {
    let fullHouseNumber = this.salonDetails.houseNumber
      ? this.salonDetails.houseNumber
      : '';
    fullHouseNumber = this.salonDetails.houseNumberSub
      ? fullHouseNumber + '/' + this.salonDetails.houseNumberSub
      : fullHouseNumber;

    const street = this.salonDetails.street
      ? this.salonDetails.street
      : '';
    const postCode = this.salonDetails.postCode
      ? this.salonDetails.postCode
      : '';
    const city = this.salonDetails.city
      ? this.salonDetails.city
      : '';

    return `${street} ${fullHouseNumber}
            ${postCode} ${city}`.trim();
  }

  private getFormattedSalonDepartments(): string {
    return this.salonDetails.departments.map((department: IDepartment) => department.name).join(', ');
  }

  private setSEO(): void {
    const thisSalon = this.route.snapshot.data.salonPage.salonDetails;
    const sub = this.route.paramMap
      .subscribe((paramMap: ParamMap) => {
        const params = {
          salonId: paramMap.get('simpleFlowSalonId'),
          cityName: paramMap.get('cityName'),
          salonName: thisSalon.name,
          departments: thisSalon.departments.map((department: IDepartment) => department.name).join(', ')
        };
        const titleKey = params.salonId ?
          'SALON-DETAILS.ROUTE-BY-SALON-ID.META-TITLE' :
          'SALON-DETAILS.ROUTE-BY-CITY-AND-ALIAS.META-TITLE';
        const descriptionKey = params.salonId ?
          'SALON-DETAILS.ROUTE-BY-SALON-ID.META-DESCRIPTION' :
          'SALON-DETAILS.ROUTE-BY-CITY-AND-ALIAS.META-DESCRIPTION';
        this.seoService.setSEO([
          {
            tagType: ISeoTagType.Title,
            i18nKey: titleKey,
            i18nParams: params
          },
          {
            tagType: ISeoTagType.Description,
            i18nKey: descriptionKey,
            i18nParams: params
          }
        ]);
      });
    this.masterSubscription.add(sub);
  }

  private createEventSubToCustomerStopDialog(): void {
    const eventSubToCustomerStopDialog = this.customerStopIntentService.openCustomerStopDialogIntent$.subscribe(
      (treatments) => {
        const cmpRef = this.ngbModal.open(CustomerStopDialogContainerComponent, {
          centered: true,
          backdrop: 'static',
          size: 'lg'
        });
        (<CustomerStopDialogContainerComponent>cmpRef.componentInstance).treatmentName = treatments.map(treatment => treatment.name).join(', ');
        (<CustomerStopDialogContainerComponent>cmpRef.componentInstance).salonId = this.salonDetails.id;
      });
    this.masterSubscription.add(eventSubToCustomerStopDialog);
  }
}
